import { useRef } from "react";
import { motion, useMotionValue, useSpring, useScroll, useTransform } from "framer-motion";
import { scrollToHash } from "@/lib/scroll";

const lineAnim = (delay) => ({
  initial: { y: "115%" },
  animate: { y: 0 },
  transition: { duration: 1.1, delay, ease: [0.16, 1, 0.3, 1] },
});

export const Hero = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 40, damping: 18 });
  const sy = useSpring(my, { stiffness: 40, damping: 18 });
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const onMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    mx.set(((e.clientX - r.left) / r.width - 0.5) * 24);
    my.set(((e.clientY - r.top) / r.height - 0.5) * 18);
  };

  return (
    <section
      id="top"
      ref={ref}
      onMouseMove={onMove}
      data-testid="hero-section"
      className="relative min-h-[100svh] flex flex-col justify-center overflow-hidden px-6 md:px-12 lg:px-16 xl:px-24 pt-16 pb-12 md:pt-20"
      style={{
        background:
          "radial-gradient(42% 36% at 88% 22%, rgba(150,32,165,0.35) 0%, transparent 66%), radial-gradient(38% 30% at 12% 4%, rgba(120,24,140,0.28) 0%, transparent 62%), radial-gradient(50% 34% at 55% 97%, rgba(110,18,120,0.30) 0%, transparent 70%), linear-gradient(165deg, #0f0111 0%, #0a010c 50%, #08000c 100%)",
      }}
    >
      {/* Iridescent chrome-ribbon artwork — approved purple visual direction, mouse parallax */}
      <motion.div
        data-testid="hero-orb"
        aria-hidden="true"
        style={{ x: sx, y: sy }}
        className="pointer-events-none absolute -inset-[4%]"
      >
        <picture>
          <source media="(max-width: 1023px)" srcSet="/brand/hero-purple-ribbons-mobile.webp" />
          <img src="/brand/hero-purple-ribbons.webp" alt="" className="w-full h-full object-cover" />
        </picture>
        {/* legibility veil over the text zone on small screens */}
        <div
          className="absolute inset-0 lg:hidden"
          style={{ background: "linear-gradient(100deg, rgba(8,0,12,0.62) 0%, rgba(8,0,12,0.35) 45%, transparent 75%)" }}
        />
        {/* Oversized DE emblem watermark bleeding off the right edge */}
        <img
          data-testid="hero-emblem-watermark"
          src="/brand/de-emblem-iridescent.png"
          alt=""
          aria-hidden="true"
          className="hidden md:block absolute top-1/2 -translate-y-1/2 -right-[16%] w-[64vw] max-w-none h-auto opacity-[0.1] select-none"
        />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 w-full">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.2 }}
          className="text-[11px] md:text-xs uppercase tracking-[0.34em] text-[#cbb8dc]/70 mb-8 md:mb-10"
        >
          Portfolio — Series 01 · Est. MMXXVI
        </motion.p>

        <div className="flex flex-col lg:flex-row lg:items-center gap-10 lg:gap-12 xl:gap-16">
          <motion.img
            data-testid="hero-emblem"
            src="/brand/de-emblem-iridescent.png"
            alt="The Danielle Edit DE emblem"
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, delay: 0.45, ease: [0.16, 1, 0.3, 1] }}
            className="w-[240px] sm:w-[320px] lg:w-[46%] h-auto self-start lg:self-center"
          />

          <div className="flex flex-col items-start text-left lg:pt-4 lg:pr-[8%] xl:pr-[10%]">
            <div className="mask-line">
              <motion.p
                data-testid="hero-mission"
                {...lineAnim(0.7)}
                className="font-serif italic text-[30px] sm:text-[36px] md:text-[44px] lg:text-[clamp(2.3rem,3.2vw,3.75rem)] leading-[1.06] text-[#efe6f4]"
              >
                Bringing your beauty
                <br className="hidden lg:block" />
                <span className="lg:hidden"> </span>to life through
                <br className="hidden lg:block" />
                <span className="lg:hidden"> </span>branding.
              </motion.p>
            </div>

            <motion.h1
              data-testid="hero-headline"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 0.9 }}
              className="mt-7 md:mt-8 font-serif uppercase text-sm md:text-lg tracking-[0.45em] md:tracking-[0.55em] text-[#f6f0e6]"
            >
              The Danielle Edit
            </motion.h1>

            <div className="mask-line mt-3">
              <motion.p
                {...lineAnim(1.0)}
                className="text-[10px] md:text-[11px] uppercase tracking-[0.2em] text-[#e9dff0]/75"
              >
                Brand Architecture · Creative Direction · Design
              </motion.p>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1.25, ease: [0.16, 1, 0.3, 1] }}
              className="mt-8 md:mt-9 flex items-center gap-8"
            >
          <button
            data-testid="hero-inquire-cta"
            onClick={() => scrollToHash("#inquire")}
            className="group relative overflow-hidden bg-[#f6f0e6] text-[#160a17] text-[11px] uppercase tracking-[0.3em] rounded-full px-9 py-4 transition-transform duration-300 hover:scale-[1.03]"
          >
            <span className="relative z-10">Inquire</span>
            <span
              aria-hidden="true"
              className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
              style={{
                background:
                  "linear-gradient(100deg, #c9a3e0, #efd9f2, #e5b8d6, #f6f0e6, #b07ad4)",
              }}
            />
          </button>
          <span className="hidden md:block text-xs text-[#efe6f4]/55 tracking-[0.2em] uppercase">
            Currently booking — Series 02
          </span>
            </motion.div>
          </div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 left-6 md:left-12 lg:left-24 flex items-center gap-4 z-10"
      >
        <span className="block w-10 h-px bg-[#f6f0e6]/30" />
        <span className="text-[10px] uppercase tracking-[0.3em] text-[#cbb8dc]/60">Scroll</span>
      </motion.div>
    </section>
  );
};
